import { useState, useEffect, useMemo, useCallback } from "react";
import { newAxiosClient } from "@shadowcue/ui";
import type { IConversationSummary } from "@shadowcue/ui";

// ── Types ────────────────────────────────────────────────────────────────────

type RegradeBannerStatus = "success" | "warning" | "error" | "info";

interface RegradeBanner {
  status: RegradeBannerStatus;
  message: string;
}

export interface RenamingConversation {
  id: number;
  name: string;
  description: string | null;
}

export interface UseConversationSummaryReturn {
  summary: IConversationSummary | null;
  loading: boolean;
  error: boolean;
  regradeBanner: RegradeBanner | null;
  setRegradeBanner: (banner: RegradeBanner | null) => void;
  regradeModalOpen: boolean;
  setRegradeModalOpen: (open: boolean) => void;
  regradeLoading: boolean;
  reportModalOpen: boolean;
  reportMarkdown: string | null;
  reportLoading: boolean;
  renamingConversation: RenamingConversation | null;
  setRenamingConversation: (conv: RenamingConversation | null) => void;
  toggleExclusion: (questionId: number, nextExcluded: boolean) => Promise<void>;
  submitRegrade: (questionIds: number[]) => Promise<void>;
  openExportModal: () => void;
  closeExportModal: () => void;
  refreshSummary: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const skippedRegradeMessage = (questionNumbers: number[]): string =>
  questionNumbers.length === 1
    ? `Question ${questionNumbers[0]} was already regraded and was skipped.`
    : `Questions ${questionNumbers.join(", ")} were already regraded and were skipped.`;

// ── Hook ─────────────────────────────────────────────────────────────────────

const useConversationSummary = (
  id: string | undefined,
  authToken: string,
): UseConversationSummaryReturn => {
  const axios = useMemo(
    () => newAxiosClient({ headers: { AUTHORIZATION: `Bearer ${authToken}` } }),
    [authToken],
  );

  const [summary, setSummary] = useState<IConversationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [regradeModalOpen, setRegradeModalOpen] = useState(false);
  const [regradeLoading, setRegradeLoading] = useState(false);
  const [regradeBanner, setRegradeBanner] = useState<RegradeBanner | null>(null);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportMarkdown, setReportMarkdown] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [renamingConversation, setRenamingConversation] =
    useState<RenamingConversation | null>(null);

  const fetchSummary = useCallback(
    () => axios.get(`/api/conversations/${id}/summary`),
    [axios, id],
  );

  useEffect(() => {
    fetchSummary()
      .then((res) => setSummary(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    // fetchSummary is included as a dependency so the effect re-runs if the
    // axios client is recreated (e.g. auth token rotation). Without it, the
    // effect would silently hold a stale closure over the old client.
  }, [fetchSummary]);

  const refreshSummary = useCallback(() => {
    fetchSummary().then((res) => setSummary(res.data));
  }, [fetchSummary]);

  const toggleExclusion = async (questionId: number, nextExcluded: boolean) => {
    try {
      await axios.patch(`/api/conversations/${id}/scoring_exclusion`, {
        excludedFromScore: nextExcluded,
        scriptTemplateQuestionId: questionId,
      });
      refreshSummary();
    } catch {
      // leave existing state on failure
    }
  };

  const submitRegrade = async (questionIds: number[]) => {
    setRegradeLoading(true);
    setRegradeBanner(null);
    try {
      const regradeRes = await axios.post(`/api/conversations/${id}/regrade`, { questionIds });
      const results: {
        questionId: number;
        updated?: boolean;
        error?: string;
        skipped?: boolean;
      }[] = regradeRes.data.results ?? [];
      const updated = results.filter((r) => r.updated);
      const failed = results.filter((r) => r.error);
      const skipped = results.filter((r) => r.skipped);

      let skippedSentence = "";
      if (skipped.length > 0 && summary) {
        let number = 0;
        const numberMap: Record<number, number> = {};
        for (const section of summary.sectionStats) {
          for (const qs of section.questionStats) {
            number += 1;
            numberMap[qs.id] = number;
          }
        }
        const skippedNumbers = skipped
          .map((r) => numberMap[r.questionId])
          .filter(Boolean);
        if (skippedNumbers.length > 0) {
          skippedSentence = skippedRegradeMessage(skippedNumbers);
        }
      }

      if (updated.length > 0 && failed.length === 0) {
        setRegradeBanner({
          status: "success",
          message: `${updated.length} question${updated.length === 1 ? "" : "s"} regraded.${skippedSentence ? ` ${skippedSentence}` : ""}`,
        });
      } else if (updated.length > 0 && failed.length > 0) {
        setRegradeBanner({
          status: "warning",
          message: `${updated.length} regraded, ${failed.length} failed — try again.${skippedSentence ? ` ${skippedSentence}` : ""}`,
        });
      } else if (updated.length === 0 && skipped.length > 0) {
        setRegradeBanner({ status: "info", message: skippedSentence });
      } else if (updated.length === 0 && failed.length > 0) {
        setRegradeBanner({
          status: "error",
          message: "Regrade failed — no scores were changed.",
        });
      } else {
        // Nothing updated, skipped, or failed — every selected question's
        // fresh grade matched its existing score, so there was nothing to change.
        setRegradeBanner({
          status: "info",
          message: "No changes — scores were already up to date.",
        });
      }

      refreshSummary();
    } catch {
      setRegradeBanner({
        status: "error",
        message: "Regrade failed — no scores were changed.",
      });
    } finally {
      setRegradeLoading(false);
      setRegradeModalOpen(false);
    }
  };

  const openExportModal = () => {
    setReportModalOpen(true);
    if (!reportMarkdown) {
      setReportLoading(true);
      axios
        .get(`/api/conversations/${id}/final_report`)
        .then((res) => setReportMarkdown(res.data.markdown))
        .finally(() => setReportLoading(false));
    }
  };

  const closeExportModal = () => setReportModalOpen(false);

  return {
    summary,
    loading,
    error,
    regradeBanner,
    setRegradeBanner,
    regradeModalOpen,
    setRegradeModalOpen,
    regradeLoading,
    reportModalOpen,
    reportMarkdown,
    reportLoading,
    renamingConversation,
    setRenamingConversation,
    toggleExclusion,
    submitRegrade,
    openExportModal,
    closeExportModal,
    refreshSummary,
  };
};

export default useConversationSummary;
